import { useEffect } from 'react';

/**
 * 화면 높이를 `--app-h`에 넣는다.
 *
 * `100dvh`로 충분하지 않다. 모바일에서 키보드가 올라오면 iOS Safari는
 * 레이아웃 뷰포트를 줄이지 않고 페이지를 위로 밀어버린다 — `dvh`는 그대로인데
 * 입력창은 키보드 뒤로 숨는다. 실제로 지금 보이는 영역을 아는 건
 * `visualViewport` 하나뿐이다.
 *
 * 안드로이드 Chrome은 `interactive-widget=resizes-content`(index.html)로
 * 레이아웃 뷰포트가 함께 줄어들지만, 그 경우에도 이 값은 같은 답을 준다.
 */
export function useAppHeight(): void {
  useEffect(() => {
    const viewport = window.visualViewport;
    const root = document.documentElement;

    const apply = () => {
      const height = viewport?.height ?? window.innerHeight;
      root.style.setProperty('--app-h', `${Math.round(height)}px`);

      /*
       * iOS는 키보드를 띄울 때 페이지를 스크롤해서 입력창을 보여준다.
       * 우리는 높이를 직접 맞추므로 그 스크롤이 남아 있으면 머리글이
       * 화면 위로 밀려 잘린다. 되돌려 놓는다.
       */
      if (window.scrollY !== 0) window.scrollTo(0, 0);
    };

    apply();

    // resize만으로는 부족하다. iOS에서 키보드가 뜨면 scroll만 오는 경우가 있다.
    viewport?.addEventListener('resize', apply);
    viewport?.addEventListener('scroll', apply);
    window.addEventListener('orientationchange', apply);

    return () => {
      viewport?.removeEventListener('resize', apply);
      viewport?.removeEventListener('scroll', apply);
      window.removeEventListener('orientationchange', apply);
      root.style.removeProperty('--app-h');
    };
  }, []);
}
